import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, FilePlus2, History, LayoutTemplate, UserCircle,
  CreditCard, Menu, X, LogOut, Activity, Building2, Users, ShieldCheck, Pill,
} from "lucide-react";
import { Button } from "./ui/button";

const DOCTOR_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/referrals/new", label: "New Referral", icon: FilePlus2 },
  { to: "/prescriptions/new", label: "New Prescription", icon: Pill },
  { to: "/history", label: "Referral History", icon: History },
  { to: "/prescriptions", label: "Prescription History", icon: History },
  { to: "/patients", label: "Patients", icon: Users },
  { to: "/templates", label: "Templates", icon: LayoutTemplate },
  { to: "/profile", label: "My Profile", icon: UserCircle },
  { to: "/billing", label: "Billing", icon: CreditCard },
];
const CENTRE_NAV = [
  { to: "/centre", label: "Referrals Inbox", icon: LayoutDashboard },
  { to: "/profile", label: "Centre Profile", icon: Building2 },
];
const PHARMACY_NAV = [
  { to: "/pharmacy", label: "Prescriptions Inbox", icon: LayoutDashboard },
  { to: "/profile", label: "Pharmacy Profile", icon: Building2 },
];
const ADMIN_NAV = [
  { to: "/admin", label: "Admin Console", icon: ShieldCheck },
];

function navForRole(role) {
  if (role === "radiology_centre") return CENTRE_NAV;
  if (role === "pharmacy") return PHARMACY_NAV;
  if (role === "admin") return ADMIN_NAV;
  return DOCTOR_NAV;
}

function TrialBanner({ subscription }) {
  const navigate = useNavigate();
  if (!subscription) return null;
  if (subscription.status === "trial") {
    const days = subscription.trial_ends_at
      ? Math.max(0, Math.ceil((new Date(subscription.trial_ends_at) - new Date()) / 86400000))
      : 0;
    return (
      <div data-testid="trial-banner" className="bg-blue-50 border-b border-blue-100 px-4 md:px-8 py-2.5 flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-blue-800 font-medium">
          <Activity className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Free trial — {days} day{days === 1 ? "" : "s"} remaining
        </p>
        <button data-testid="banner-upgrade-btn" onClick={() => navigate("/billing")} className="text-sm font-semibold text-blue-700 hover:text-blue-900 underline underline-offset-2">
          Upgrade to Professional
        </button>
      </div>
    );
  }
  if (subscription.status === "trial_expired" || subscription.status === "expired" || subscription.status === "cancelled") {
    return (
      <div data-testid="expired-banner" className="bg-amber-50 border-b border-amber-200 px-4 md:px-8 py-2.5 flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-amber-800 font-medium">Your subscription is inactive. Renew to create new referrals.</p>
        <button data-testid="banner-renew-btn" onClick={() => navigate("/billing")} className="text-sm font-semibold text-amber-900 hover:underline">Renew now</button>
      </div>
    );
  }
  return null;
}

export default function Layout({ children }) {
  const { user, subscription, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = navForRole(user?.role);

  const Sidebar = (
    <aside className="w-64 bg-slate-50 border-r border-slate-200 flex flex-col h-full">
      <div className="px-6 py-6 flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
          <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <span className="text-xl font-bold tracking-tight text-slate-900 font-head">ReferU</span>
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-200/70"
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-200">
        <div className="px-3 py-2 mb-2">
          <p className="text-sm font-semibold text-slate-900 truncate">{user?.full_name}</p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
        </div>
        <Button data-testid="logout-btn" onClick={logout} variant="ghost" className="w-full justify-start gap-3 text-slate-600 hover:text-red-600 rounded-xl h-11">
          <LogOut className="w-5 h-5" /> Log out
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-white flex">
      <div className="hidden md:block fixed inset-y-0 left-0 z-30">{Sidebar}</div>

      {open && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 z-50">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
        <header className="md:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Activity className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-lg font-bold text-slate-900 font-head">ReferU</span>
          </div>
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="p-2 text-slate-700">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </header>

        {user?.role === "doctor" && <TrialBanner subscription={subscription} />}

        <main className="flex-1 referu-scroll">{children}</main>
      </div>
    </div>
  );
}
